import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, getAuthHeader } from '../../App';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { AlertTriangle, Download } from 'lucide-react';
import { SECTIONS, MOVEMENT_LABELS, POSSESSION_STATUS, monthRows, cellValue, fmtMoney } from './leaseHistoryConfig';

const Pill = ({ map, value }) => {
  const [label, cls] = map[value] || [value || '-', 'bg-slate-50 text-slate-600 border-slate-200'];
  return <Badge variant="outline" className={`text-[11px] font-medium whitespace-nowrap ${cls}`}>{label}</Badge>;
};

const renderCell = (row, col) => {
  if (col[0] === 'movement') return <Pill map={MOVEMENT_LABELS} value={row.movement} />;
  if (col[0] === 'status' && POSSESSION_STATUS[row.status]) return <Pill map={POSSESSION_STATUS} value={row.status} />;
  return cellValue(row, col);
};

const openCertificate = async (url, filename) => {
  try {
    const resp = await axios.get(`${API.replace(/\/api$/, '')}${url}`, { headers: getAuthHeader(), responseType: 'blob' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
    link.download = filename;
    link.click();
  } catch (e) {
    toast.error('Failed to download the form PDF');
  }
};

const SectionTable = ({ sectionKey, rows, testId, filter }) => {
  const sec = SECTIONS[sectionKey];
  const withForm = sectionKey === 'handovers' || sectionKey === 'takeovers';
  const shown = filter ? rows.filter(filter) : rows;
  if (!shown.length) return <div className="py-8 text-center text-sm text-slate-400" data-testid={`${testId}-empty`}>No records for this month.</div>;
  const total = sec.amountKey ? shown.reduce((a, r) => a + Number(r[sec.amountKey] || 0), 0) : null;
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white" data-testid={testId}>
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            {sec.columns.map((c) => <TableHead key={c[0]} className="text-xs font-semibold text-slate-600">{c[1]}</TableHead>)}
            {withForm && <TableHead className="text-xs font-semibold text-slate-600">Form</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {shown.map((r, i) => (
            <TableRow key={r.id || `${r.bike_plate_number}-${i}`} className="text-sm">
              {sec.columns.map((c) => (
                <TableCell key={c[0]} className={c[0].includes('plate') ? 'font-mono text-xs' : ''}>{renderCell(r, c)}</TableCell>
              ))}
              {withForm && (
                <TableCell>
                  {r.certificate_url ? (
                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => openCertificate(r.certificate_url, `${sectionKey}_${r.bike_plate_number || r.id}.pdf`)} data-testid={`${sectionKey}-form-${r.id}`}>
                      <Download className="w-3.5 h-3.5" />
                    </Button>
                  ) : '-'}
                </TableCell>
              )}
            </TableRow>
          ))}
          {total !== null && (
            <TableRow className="bg-slate-50 font-semibold">
              <TableCell colSpan={sec.columns.length - 2}>Total</TableCell>
              <TableCell colSpan={2} data-testid={`${testId}-total`}>AED {fmtMoney(total)}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

const BIKE_VIEWS = [
  ['all', 'All in possession', null],
  ['new', 'New this month', (b) => b.status === 'handed over this month'],
  ['out', 'Taken over', (b) => b.status === 'permanently taken over / moved this month'],
  ['swaps', 'Swaps only', (b) => b.status === 'seen in battery swaps (no handover on file)'],
];

const BikesTab = ({ m }) => {
  const [view, setView] = useState('all');
  const rows = m.possession_motorcycles || [];
  const swapOnly = m.counts.possession_swap_only || 0;
  return (
    <div className="space-y-3">
      {swapOnly > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid={`swap-only-note-${m.month_key}`}>
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          {swapOnly} bike{swapOnly > 1 ? 's were' : ' was'} used by this lessee's riders (battery swaps) but {swapOnly > 1 ? 'have' : 'has'} no handover form on file.
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {BIKE_VIEWS.map(([k, label, fn]) => (
          <button
            key={k}
            type="button"
            onClick={() => setView(k)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${view === k ? 'border-green-600 bg-green-600 text-white' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
            data-testid={`bike-view-${k}-${m.month_key}`}
          >
            {label} ({fn ? rows.filter(fn).length : rows.length})
          </button>
        ))}
      </div>
      <SectionTable sectionKey="possession" rows={rows} filter={BIKE_VIEWS.find((v) => v[0] === view)[2]} testId={`possession-table-${m.month_key}`} />
    </div>
  );
};

export const LeaseHistoryMonthDetail = ({ m }) => {
  const c = m.counts;
  const tabs = [
    ['bikes', `Bikes (${c.possession ?? 0})`],
    ['moves', `Handovers & Takeovers (${(m.handovers || []).length + (m.takeovers || []).length})`],
    ['fines', `Fines (${c.fines})`],
    ['salik', `SALIK (${c.salik})`],
    ['maintenance', `Maintenance (${c.maintenance})`],
  ];
  return (
    <Tabs defaultValue="bikes" className="w-full" data-testid={`month-detail-${m.month_key}`}>
      <TabsList className="mb-3 h-auto flex-wrap bg-slate-100">
        {tabs.map(([v, label]) => <TabsTrigger key={v} value={v} className="text-xs" data-testid={`month-tab-${v}-${m.month_key}`}>{label}</TabsTrigger>)}
      </TabsList>
      <TabsContent value="bikes"><BikesTab m={m} /></TabsContent>
      <TabsContent value="moves" className="space-y-4">
        <div>
          <div className="mb-1.5 text-xs font-semibold text-slate-600">Handovers - {c.handovers} counted{c.rider_changes ? `, ${c.rider_changes} rider changes not counted` : ''}</div>
          <SectionTable sectionKey="handovers" rows={m.handovers || []} testId={`handovers-table-${m.month_key}`} />
        </div>
        <div>
          <div className="mb-1.5 text-xs font-semibold text-slate-600">Takeovers - {c.takeovers} counted (permanent / moved){c.temporary_takeovers ? `, ${c.temporary_takeovers} temporary not counted` : ''}</div>
          <SectionTable sectionKey="takeovers" rows={m.takeovers || []} testId={`takeovers-table-${m.month_key}`} />
        </div>
      </TabsContent>
      {['fines', 'salik', 'maintenance'].map((k) => (
        <TabsContent key={k} value={k}><SectionTable sectionKey={k} rows={monthRows(m, k)} testId={`${k}-table-${m.month_key}`} /></TabsContent>
      ))}
    </Tabs>
  );
};
